import { useMemo, useRef, useEffect } from 'react';
import {
  MolecularWorkspace,
  MolecularSVGProvider,
  useMolecularSVG,
  useSVGGeneration,
  useZoomPan,
  useFormatOverride,
  renderBondFromRecipe,
  ZoomPanControls,
  Defs,
  applyZoomToViewBox,
  parseViewBoxSafe,
  viewBoxToString,
  HTMLStructureTree,
  buildStructure,
  buildAltTextMapFromStructure,
  collectHiddenAtomIds,
} from '@alchemiesolns/svg-interactive';
import { fromConfigString } from '@alchemiesolns/workspace-serialization';
import { extractWorkspaceFeatures } from '@alchemiesolns/feature-extractor-compat';
import {
  DEFAULT_INTERACTIVE_MOLECULE_INITIAL_ZOOM,
  SCHEME_DISPLAY_ZOOM_DEFAULT,
  SCHEME_DISPLAY_ZOOM_MAX,
  SCHEME_DISPLAY_ZOOM_MIN,
} from '../constants/reactionScheme';
import { REACTION_LAYOUT_BASE } from '../utils/exportLayout';

export interface RenderingMetrics {
  effectiveFontSize: number;  // Font size in pixels
  effectiveBondWidth: number;  // Bond width in pixels
  svgScale: number;           // SVG units to pixels conversion
  /** Bond width (px) if this viewBox were shown in the reference reaction slot at zoom 1 */
  referenceEffectiveBondWidth: number;
}

interface InteractiveMoleculeProps {
  config: string;
  svgWidth: number;
  svgHeight: number;
  /** Layout "fit" viewBox from normalization (never a saved zoomed viewport). */
  layoutViewBox: string;
  /** Optional saved viewport from JSON / user pan-zoom; hydrated without re-applying `initialZoomFactor` to the fit box. */
  persistedViewBox?: string | null;
  /** Reaction scheme font/zoom; applied as additional viewBox zoom so persisted viewports scale too. */
  schemeDisplayZoom?: number;
  onViewBoxChange?: (viewBox: string | null) => void;
  onZoomChange?: (zoom: number) => void;
  onRenderingMetrics?: (metrics: RenderingMetrics) => void;
  interactionMode?: 'enabled' | 'sidebarOnly';
  alignZoomControls?: 'left' | 'right' | 'outside-left' | 'outside-right' | 'none';
  ariaLabel?: string;
  onControlsReady?: (controls: unknown | null) => void;
  /** Zoom after fit (1 = fit). See `DEFAULT_INTERACTIVE_MOLECULE_INITIAL_ZOOM`. */
  initialZoomFactor?: number;
}

/**
 * Inner component that renders inside MolecularSVGProvider
 * so it has access to context.
 */
function InteractiveMoleculeInner({
  layoutViewBox,
  persistedViewBox,
  schemeDisplayZoom,
  onViewBoxChange,
  onZoomChange,
  onRenderingMetrics,
  interactionMode,
  alignZoomControls,
  ariaLabel,
  onControlsReady,
  initialZoomFactor,
}: {
  alignZoomControls?: 'left' | 'right' | 'outside-left' | 'outside-right' | 'none';
  layoutViewBox: string;
  persistedViewBox: string | null;
  schemeDisplayZoom: number;
  onViewBoxChange?: (viewBox: string | null) => void;
  onZoomChange?: (zoom: number) => void;
  onRenderingMetrics?: (metrics: RenderingMetrics) => void;
  interactionMode: 'enabled' | 'sidebarOnly';
  ariaLabel?: string;
  onControlsReady?: (controls: unknown | null) => void;
  initialZoomFactor: number;
}) {
  const { workspace, svgWidth, svgHeight } = useMolecularSVG();
  // `calculatedViewBox` from this hook is unused — viewport comes from `layoutViewBox` + useZoomPan.
  // Keep alignment fixed so geometry does not depend on a redundant prop.
  const { atoms, bondRecipes, electrons, charges, arrows } = useSVGGeneration(
    workspace,
    svgWidth,
    svgHeight,
    'center'
  );
  const format = useFormatOverride();

  // Build structure for HTMLStructureTree (accessibility export)
  const featureResult = useMemo(() => {
    if (!workspace) return null;
    try {
      return extractWorkspaceFeatures(workspace);
    } catch {
      return null;
    }
  }, [workspace]);

  const structure = useMemo(() => {
    if (!workspace || !featureResult) return null;
    try {
      return buildStructure(workspace, featureResult);
    } catch {
      return null;
    }
  }, [workspace, featureResult]);

  // MC-886: filter hidden atoms out of the navigation tree.
  const hiddenAtomIds = useMemo(
    () => (workspace ? collectHiddenAtomIds(workspace) : undefined),
    [workspace]
  );

  // PIPH-132: Pass featureResult and workspace for functional group descriptions
  const treeLabelMap = useMemo(() => {
    if (!structure) return null;
    try {
      return buildAltTextMapFromStructure(structure, featureResult ?? undefined, workspace);
    } catch {
      return null;
    }
  }, [structure, featureResult, workspace]);

  const expectedDefaultViewBox = useMemo(() => {
    const fit = parseViewBoxSafe(layoutViewBox);
    const def = applyZoomToViewBox(fit, initialZoomFactor, 0.5, 0.5, fit, 0, 8);
    return viewBoxToString(def);
  }, [layoutViewBox, initialZoomFactor]);

  const {
    viewBox,
    svgRef,
    backgroundHandlers,
    svgHandlers,
    controls,
    state,
  } = useZoomPan({
    calculatedViewBox: layoutViewBox,
    initialViewport: persistedViewBox ?? undefined,
    initialZoomFactor,
    config:
      interactionMode === 'sidebarOnly'
        ? { panEnabled: true, zoomEnabled: false }
        : undefined,
    enableMouseWheelZoom: false, // Disable scroll wheel zoom
    minZoom: 0, // Match scheme zoom % down to 0 (useZoomPan applies no extra floor)
    maxZoom: 8,
    onReset: () => {
      // Clear the user's modified viewBox when reset is clicked (set to null)
      // This allows the preview to fall back to the normalized viewBox
      onViewBoxChange?.(null);
    },
  });

  const canonViewBox = (s: string) => viewBoxToString(parseViewBoxSafe(s));

  // Report viewBox overrides only when the viewport differs from the default (fit + initialZoom).
  const prevViewBox = useRef(viewBox);
  const prevLayoutRef = useRef<string | null>(null);
  const hasReportedControls = useRef(false);
  useEffect(() => {
    if (interactionMode === 'sidebarOnly') return;
    if (prevLayoutRef.current !== layoutViewBox) {
      prevLayoutRef.current = layoutViewBox;
      prevViewBox.current = viewBox;
      return;
    }
    if (viewBox === prevViewBox.current) return;
    prevViewBox.current = viewBox;
    if (canonViewBox(viewBox) !== canonViewBox(expectedDefaultViewBox)) {
      onViewBoxChange?.(viewBox);
    } else {
      onViewBoxChange?.(null);
    }
  }, [interactionMode, viewBox, layoutViewBox, expectedDefaultViewBox, onViewBoxChange]);

  useEffect(() => {
    if (!onControlsReady || hasReportedControls.current || !controls) return;
    hasReportedControls.current = true;
    onControlsReady?.(controls);
  }, [controls, onControlsReady]);

  useEffect(() => {
    return () => onControlsReady?.(null);
  }, [onControlsReady]);

  const prevSchemeZoomRef = useRef<number>(1);

  // The live viewport, mirrored so the re-base below can read it WITHOUT taking a
  // dependency on it. Depending on `viewBox` would re-base on every pan/zoom and
  // reintroduce the feedback loop the effect's own comment warns about.
  const liveViewBoxRef = useRef(viewBox);
  liveViewBoxRef.current = viewBox;

  useEffect(() => {
    // Re-base only when the normalized layout changes. Re-basing on every persisted viewport
    // update causes a zoom feedback loop: persist -> re-base -> zoom -> persist -> ...
    //
    // Re-base to what the viewport ACTUALLY shows, not to 1 (PIPH-330). Assuming
    // 1 was only true while the shared `useZoomPan` auto-RESET the viewport on
    // every `calculatedViewBox` change. That reset was removed — it snapped the
    // mechanisms player back to Fit-All on every arrow push — so a layout change
    // now leaves the user's zoom in place while this ref claimed a fresh
    // baseline. The sidebar applies zoom as a RATIO against this ref, so the
    // same zoom got applied a second time: 80% became 64%, and asking for 100%
    // landed back on 80% — "shrinks instead of grows".
    //
    // Units matter here. `schemeDisplayZoom` is measured against the initial
    // fit, so a viewport sitting at `initialZoomFactor` IS scheme zoom 1 (the
    // default initial zoom is 0.8, not 1). Divide to convert, or this trades one
    // unit bug for another.
    const fit = parseViewBoxSafe(layoutViewBox);
    const shown = parseViewBoxSafe(liveViewBoxRef.current);
    const zoomFactor = shown.width > 0 ? fit.width / shown.width : initialZoomFactor;
    prevSchemeZoomRef.current =
      initialZoomFactor > 0 && Number.isFinite(zoomFactor) ? zoomFactor / initialZoomFactor : 1;
  }, [layoutViewBox, initialZoomFactor]);

  useEffect(() => {
    if (!controls) return;
    const prev = prevSchemeZoomRef.current;
    const next = schemeDisplayZoom;
    if (!Number.isFinite(prev) || !Number.isFinite(next)) {
      prevSchemeZoomRef.current = next;
      return;
    }
    if (Math.abs(prev - next) < 1e-9) return;
    const safePrev = Math.max(1e-9, prev);
    const safeNext = Math.max(1e-9, next);
    const ratio = safeNext / safePrev;
    if (!Number.isFinite(ratio) || ratio <= 0 || Math.abs(ratio - 1) < 1e-9) {
      prevSchemeZoomRef.current = next;
      return;
    }
    controls.zoom(ratio, 0.5, 0.5);
    prevSchemeZoomRef.current = next;
  }, [controls, schemeDisplayZoom]);

  // Report zoom changes to parent
  useEffect(() => {
    onZoomChange?.(state.zoomFactor);
  }, [state.zoomFactor, onZoomChange]);

  // Calculate and report rendering metrics
  useEffect(() => {
    if (!onRenderingMetrics || !viewBox) return;

    // Parse viewBox to get SVG coordinate dimensions
    const parsed = parseViewBoxSafe(viewBox);
    const vbWidth = parsed.width;
    const vbHeight = parsed.height;
    if (!Number.isFinite(vbWidth) || !Number.isFinite(vbHeight) || vbWidth <= 0 || vbHeight <= 0) return;

    // Calculate SVG to pixel scale (how many pixels per SVG unit)
    const horizontalScale = svgWidth / vbWidth;
    const verticalScale = svgHeight / vbHeight;
    const svgScale = Math.min(horizontalScale, verticalScale); // Use min to maintain aspect ratio

    const refW = REACTION_LAYOUT_BASE.moleculeWidth;
    const refH = REACTION_LAYOUT_BASE.moleculeHeight;
    const referenceSvgScale = Math.min(refW / vbWidth, refH / vbHeight);

    // Get format constants and apply both the viewBox scale and zoom
    const effectiveFontSize = format.fontSize * svgScale * state.zoomFactor;
    const effectiveBondWidth = format.bond.thickness * svgScale * state.zoomFactor;
    const referenceEffectiveBondWidth = format.bond.thickness * referenceSvgScale;

    onRenderingMetrics({
      effectiveFontSize,
      effectiveBondWidth,
      svgScale: svgScale * state.zoomFactor,
      referenceEffectiveBondWidth,
    });
  }, [viewBox, state.zoomFactor, format, svgWidth, svgHeight, onRenderingMetrics]);

  // Parse viewBox for background rect
  const bgRect = useMemo(() => {
    const parsed = parseViewBoxSafe(viewBox);
    return {
      x: parsed.x,
      y: parsed.y,
      width: parsed.width,
      height: parsed.height,
      fill: 'transparent' as const,
    };
  }, [viewBox]);

  return (
    <div style={{ position: 'relative', width: svgWidth, height: svgHeight, display: "flex" }}>
      {interactionMode !== 'sidebarOnly' && alignZoomControls === 'left' && (
        <div style={{ height: '100%', display: 'flex', alignItems: 'flex-end' }}>
          <ZoomPanControls controls={controls} buttonSize={24} />
        </div>
      )}
      <svg
        ref={svgRef}
        width={svgWidth}
        height={svgHeight}
        viewBox={viewBox}
        aria-label={ariaLabel || "Interactive molecular structure diagram with zoom and pan controls"}
        style={{
          cursor: state.isPanning ? 'grabbing' : 'grab',
        }}
        {...(interactionMode === 'sidebarOnly' ? {} : svgHandlers)}
      >
        <Defs />
        {/* Background rect remains interactive for panning */}
        <rect {...bgRect} {...backgroundHandlers} />

        {/* All molecular content is non-interactive */}
        <g style={{ pointerEvents: 'none' }}>
          {bondRecipes.flatMap((recipe) => {
            const atom1 = atoms.find((a) => a.id === recipe.atom1Id);
            const atom2 = atoms.find((a) => a.id === recipe.atom2Id);
            if (!atom1 || !atom2) return [];
            if (atom1.visibility === 'hidden' || atom2.visibility === 'hidden') return [];
            return renderBondFromRecipe(recipe, atom1.x, atom1.y, atom2.x, atom2.y, format);
          })}

          {atoms.map((atom) => (
            <g
              key={atom.id}
              transform={`translate(${atom.x}, ${atom.y})`}
              style={{ display: atom.visibility === 'hidden' ? 'none' : undefined }}
            >
              {atom.graphics}
            </g>
          ))}

          {electrons}
          {charges}
          {arrows}
        </g>

        {/* HTMLStructureTree for accessibility export - renders inside foreignObject. */}
        {structure && treeLabelMap && (
          <HTMLStructureTree
            structure={structure}
            treeLabelMap={treeLabelMap}
            hiddenAtomIds={hiddenAtomIds}
            defaultExpandAll
          />
        )}
      </svg>
      {interactionMode !== 'sidebarOnly' && alignZoomControls === 'right' && (
        <div style={{ height: '100%', display: 'flex', alignItems: 'flex-end' }}>
          <ZoomPanControls controls={controls} buttonSize={24} />
        </div>
      )}
      {interactionMode !== 'sidebarOnly' && alignZoomControls === 'outside-left' && (
        <div style={{ position: 'absolute', left: '-30px', top: '50%', transform: 'translateY(-50%)', zIndex: 2 }}>
          <ZoomPanControls controls={controls} buttonSize={24} />
        </div>
      )}
      {interactionMode !== 'sidebarOnly' && alignZoomControls === 'outside-right' && (
        <div style={{ position: 'absolute', right: '-30px', top: '50%', transform: 'translateY(-50%)', zIndex: 2 }}>
          <ZoomPanControls controls={controls} buttonSize={24} />
        </div>
      )}
    </div>
  );
}

/**
 * Interactive molecule viewer with zoom/pan support.
 * Uses a normalized viewBox as its initial viewport so that
 * paired molecules display at the same physical scale.
 */
export function InteractiveMolecule({
  config,
  svgWidth,
  svgHeight,
  layoutViewBox,
  persistedViewBox = null,
  schemeDisplayZoom = SCHEME_DISPLAY_ZOOM_DEFAULT,
  onViewBoxChange,
  onZoomChange,
  onRenderingMetrics,
  interactionMode = 'enabled',
  alignZoomControls = 'left',
  ariaLabel,
  onControlsReady,
  initialZoomFactor = DEFAULT_INTERACTIVE_MOLECULE_INITIAL_ZOOM,
}: InteractiveMoleculeProps) {
  const schemeZoomClamped = Math.min(
    SCHEME_DISPLAY_ZOOM_MAX,
    Math.max(SCHEME_DISPLAY_ZOOM_MIN, schemeDisplayZoom)
  );
  const effectiveInitialZoomFactor = useMemo(
    () => Math.min(8, Math.max(1e-3, initialZoomFactor)),
    [initialZoomFactor]
  );

  const workspace = useMemo(() => {
    if (!config) return new MolecularWorkspace();
    try {
      return fromConfigString(config);
    } catch {
      return new MolecularWorkspace();
    }
  }, [config]);

  return (
    <MolecularSVGProvider
      workspace={workspace}
      svgWidth={svgWidth}
      svgHeight={svgHeight}
      padding={0}
      alignment="center"
    >
      <InteractiveMoleculeInner
        layoutViewBox={layoutViewBox}
        persistedViewBox={persistedViewBox}
        schemeDisplayZoom={schemeZoomClamped}
        onViewBoxChange={onViewBoxChange}
        onZoomChange={onZoomChange}
        onRenderingMetrics={onRenderingMetrics}
        interactionMode={interactionMode}
        alignZoomControls={alignZoomControls}
        ariaLabel={ariaLabel}
        onControlsReady={onControlsReady}
        initialZoomFactor={effectiveInitialZoomFactor}
      />
    </MolecularSVGProvider>
  );
}
